/**
 * ProductionEmailProvider — isolated production email delivery.
 *
 * This adapter delegates to an injectable transport function.
 * When no transport is explicitly set, it auto-wires a
 * transport that calls the project's `send-email` edge function
 * via the Supabase client, keeping the codebase vendor-agnostic.
 * Swap in Resend, SendGrid, Postmark, AWS SES, etc. by calling
 * setTransport() with a custom transport.
 */

import type { EmailMessage, EmailResult, EmailProvider } from './types';
import { getSupabase } from '@/lib/supabase';

export type EmailTransport = (message: EmailMessage) => Promise<EmailResult>;

const defaultTransport: EmailTransport = async (message: EmailMessage): Promise<EmailResult> => {
  try {
    const supabase = getSupabase();
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to: message.to,
        from: message.from,
        replyTo: message.replyTo,
        subject: message.subject,
        htmlBody: message.htmlBody,
        textBody: message.textBody,
      },
    });

    if (error) {
      return {
        success: false,
        provider: 'production',
        status: 'failed',
        error: error.message,
      };
    }

    if (data && data.success === true) {
      return {
        success: true,
        messageId: data.messageId,
        provider: 'production',
        status: 'sent',
        sentAt: data.sentAt ?? new Date().toISOString(),
      };
    }

    return {
      success: false,
      provider: 'production',
      status: 'failed',
      error: data?.error ?? 'Unknown error from edge function',
    };
  } catch (err) {
    return {
      success: false,
      provider: 'production',
      status: 'failed',
      error: err instanceof Error ? err.message : String(err),
    };
  }
};

export class ProductionEmailProvider implements EmailProvider {
  readonly name = 'production';
  private transport: EmailTransport | null = null;

  setTransport(transport: EmailTransport): void {
    this.transport = transport;
  }

  async send(message: EmailMessage): Promise<EmailResult> {
    const transport = this.transport ?? defaultTransport;
    return transport(message);
  }

  async isAvailable(): Promise<boolean> {
    return true;
  }
}
